/**
 * Structured logger that is silent in production and verbose in development.
 *
 * Replaces scattered console.log/error calls with a consistent interface.
 * In production builds (import.meta.env.PROD), only warn/error are emitted.
 * Errors are automatically forwarded to Sentry when configured.
 */

import { captureError } from './sentry';

const isDev = import.meta.env.DEV;

function formatPrefix(scope: string): string {
  return `[${scope}]`;
}

export function createLogger(scope: string) {
  const prefix = formatPrefix(scope);

  return {
    /** Debug-level: only visible in development */
    debug(...args: unknown[]) {
      if (isDev) console.debug(prefix, ...args);
    },

    /** Info-level: only visible in development */
    info(...args: unknown[]) {
      if (isDev) console.log(prefix, ...args);
    },

    /** Warning: always visible */
    warn(...args: unknown[]) {
      console.warn(prefix, ...args);
    },

    /** Error: always visible, also reported to Sentry */
    error(...args: unknown[]) {
      console.error(prefix, ...args);
      // Forward the first Error-like argument to Sentry
      const err = args.find((a) => a instanceof Error);
      if (err) {
        captureError(err, { scope, extra: args.filter((a) => !(a instanceof Error)) });
      }
    },
  };
}

/** General-purpose app logger */
export const log = createLogger('App');
